import {JOB_EVENT_LIST} from "../testing/mockdata/MockDataJobEvent";
import {JOB_LIST} from "../testing/mockdata/MockDataJob";
import {VEHICLE_LIST} from "../testing/mockdata/MockDataVehicle";
import {ACCOUNT_LIST} from "../testing/mockdata/MockDataAccount";
import {INSTALLER_LIST} from "../testing/mockdata/MockDataInstaller";
import {VEHICLE_INSTALLER_EVENT} from "../testing/mockdata/MockDataVehicleInstallerEvent";

// internal helpers

function deepCopy(toCopy) {
    return JSON.parse(JSON.stringify(toCopy));
}

function formatDate(dateString) {
    const [day, month, year] = dateString.split('/');
    return new Date(`${year}-${month}-${day}`);
}

function GetRelatedJobEvents(jobID) {
    return deepCopy(JOB_EVENT_LIST).filter(event => {
        return event.jobID === jobID;
    });
}

function AddJobEventDetails() {
    return function (job) {
        job["dates"] = {};
        const events = GetRelatedJobEvents(job.ID);
        events.forEach((event) => {
            switch (event.eventType) {
                case "installerAssigned":
                    job.dates["installerAssigned"] = event.date;
                    break;
                case "installationComplete":
                    job.dates["installationComplete"] = event.date;
                    break;
                case "productionComplete":
                    job.dates["productionComplete"] = event.date;
                    break;
                default:
                    break;
            }
        });
        return job;
    };
}

function FilterJobs(type) {
    return deepCopy(JOB_LIST).filter(job => {
        return job.installationStatus === type;
    }).map(AddJobEventDetails());
}


// exported helpers

export const initialJobListState = {
    filterOptions: {
        customerName: "",
        installerName: "",
        startDate: "",
        endDate: "",
    },
};

export function SkimJobList() {
    return {
        unassignedList: sortListByDate(FilterJobs("UNASSIGNED"), "productionComplete", "desc").slice(0, 4),
        assignedList: sortListByDate(FilterJobs("ASSIGNED"), "installerAssigned", "desc").slice(0, 4),
        completedList: sortListByDate(FilterJobs("COMPLETED"), "installationComplete", "desc").slice(0, 4),
    }
}

export function UnassignedJobList() {
    return FilterJobs("UNASSIGNED");
}

export function AssignedJobList() {
    return FilterJobs("ASSIGNED")
}

export function CompletedJobList() {
    return FilterJobs("COMPLETED")
}

export function sortListByDate(list, objectIdentifier, order = 'asc') {
    const sortOrder = (order === 'desc') ? -1 : 1;
    return list.sort((a, b) => {
        const dateA = formatDate(a.dates[objectIdentifier]);
        const dateB = formatDate(b.dates[objectIdentifier]);
        const comparison = (dateA > dateB) ? 1 : (dateA < dateB) ? -1 : 0;
        return comparison * sortOrder;
    });
}

export function FilterQueryJobs(jobs, filterOptions, dateFieldIdentifier) {
    const {customerName, installerName, jobName, startDate, endDate} = filterOptions;

    let installer = null;
    let customer = null;
    let selectedJob = null;
    let notfound = false;

    if (installerName) {
        installer = ACCOUNT_LIST.find(account => account.name.toLowerCase() === installerName.toLowerCase());
        if (!installer) {
            notfound = true;
        }
    }

    if (customerName) {
        customer = ACCOUNT_LIST.find(account => account.name.toLowerCase() === customerName.toLowerCase());
        if (!customer) {
            notfound = true;
        }
    }


    if (jobName) {

        selectedJob = JOB_LIST.find(job => job.name.toLowerCase() === jobName.toLowerCase());

        if (!selectedJob) {
            notfound = true;
        }
    }

    if (notfound) {
        return [];
    }

    return jobs.filter((job) => {
        const jobDate = formatDate(job.dates[dateFieldIdentifier]);
        const startDateObj = formatDate(startDate);
        const endDateObj = formatDate(endDate);

        const filterByInstaller = installer && VEHICLE_LIST.some(vehicle => vehicle.installerID === installer.ID && vehicle.JobID === job.ID);
        const filterByCustomer = customer && job.CustomerID === customer.ID;
        const filterByJob = selectedJob && job.ID === selectedJob.ID;
        const filterByStartDate = jobDate >= startDateObj;
        const filterByEndDate = jobDate <= endDateObj;

        return (
            (!installer || filterByInstaller) &&
            (!customer || filterByCustomer) &&
            (!selectedJob || filterByJob) &&
            filterByStartDate &&
            filterByEndDate
        );
    });
}

export function InstallerList() {
    const installerList = deepCopy(INSTALLER_LIST);

    installerList.forEach((installer, i) => {
        const acc = deepCopy(ACCOUNT_LIST).filter(acc => {
            return acc.ID === installer.accountID;
        })[0];
        delete acc['ID'];
        installerList.splice(i, 1, Object.assign({}, installer, acc));
    });

    return [installerList, null];
}

export function AccountList() {
    return deepCopy(ACCOUNT_LIST);
}

export function InstallerListVehicleEvents() {
    return deepCopy(VEHICLE_INSTALLER_EVENT);
}

export function SingleInstallerListVehicleEvents(installerID, events) {
    const allEvents = events;
    return allEvents.filter(event => { return event.installerID === installerID; });
}

export function addNewEvent(installerID, event) {
    
    const events = InstallerListVehicleEvents(installerID);
    events.push(event)

    return events
}

export function SortInstallerListByVehicleEvents(installerList, date, events) {
    
    installerList = installerList[0]

    const filteredEvents = events.filter(
        (event) => event.start.slice(0, 10) === date
    );

    const installerHours = {};
    const installerEvents = {};

    filteredEvents.forEach((event) => {
        const installerID = event.installerID;
        const start = new Date(event.start);
        const end = new Date(event.end);
        const hours = (end - start) / 1000 / 60 / 60;

        if (installerHours[installerID]) {
            installerHours[installerID] += hours;
        } else {
            installerHours[installerID] = hours;
        }

        if (installerEvents[installerID]) {
            installerEvents[installerID] += 1;
        } else {
            installerEvents[installerID] = 1;
        }
    });

    const sortedInstallerList = installerList.sort((a, b) => {
        const aHours = installerHours[a.accountID] || 0;
        const bHours = installerHours[b.accountID] || 0;
        return aHours - bHours;
    });

    const installerJobs = {};
    sortedInstallerList.forEach((installer) => {
        installerJobs[installer.accountID] = installerEvents[installer.accountID] || 0;
    });

    return [sortedInstallerList, installerJobs];
}


export function VehicleList() {
    return deepCopy(VEHICLE_LIST)
}

export function VehiclesAssociatedWithJobList(VehicleList, jobId) {
    return VehicleList.filter(vehicle => {
        return vehicle.JobID === jobId;
    });
}

export function deleteEvent(selectedEvent) {

    const events = InstallerListVehicleEvents()

    const filteredEvents = events.filter((event) => event.eventID !== selectedEvent.eventID);

    return filteredEvents
}

export function deleteJob(selectedJob) {

    const jobs = UnassignedJobList()

    const filteredJobs = jobs.filter((job) => job.ID !== selectedJob.ID);

    return filteredJobs
}